import { MarketLevelMarketTemplateId } from '@ps/web-sports-api-types';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import { MarketTemplate, OddsBoostEventLink, OddsBoostPanelHeader } from '@swf/sports/components';
import EventStructuredData from '@swf/sports/components/EventStructuredData/EventStructuredData';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';

import styles from './SportsOddsBoostWidget.scss';
import Skeleton from './components/LoadingState/LoadingState';
import { sportsOddsBoostWidgetModel, useSportsOddsBoostWidget } from './model';

const SportsOddsBoostWidget: WidgetFC = () => {
  const { widgetSelect, widgetDispatch } = useSportsOddsBoostWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const hasErrored = useSelector(widgetSelect.hasErrored);
  const events = useSelector(widgetSelect.events);
  const marketIds = useSelector(widgetSelect.marketIds);
  const markets = useSelector(widgetSelect.markets);
  const marketGroups = useSelector(widgetSelect.groups);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const competitions = useSelector(widgetSelect.competitions);

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) {
    return <Skeleton />;
  }

  if (hasErrored || !Object.keys(marketGroups).length) {
    return null;
  }

  return (
    <>
      <OddsBoostPanelHeader />
      {marketGroups.map((marketGroup) => {
        const market = markets[marketGroup[0]];

        if (!market || isEmpty(market)) {
          return null;
        }

        const { eventId, eventTypeId, marketId, competitionId } = market;
        const competition = competitionId ? competitions[competitionId] : undefined;
        const eventType = eventTypes[eventTypeId];
        const event = events[eventId];

        if (!eventType) return null;

        return (
          <div className={styles.marketTemplateWrapper} key={`${eventId}-${marketId}`}>
            <OddsBoostEventLink
              event={event}
              market={market}
              eventTypeSlug={eventType.eventTypeSlug}
              competitionSlug={competition?.competitionSlug}
            />
            {marketGroup.map((marketId) => {
              const market = markets[marketId];
              if (!market) return null;

              return (
                <MarketTemplate
                  key={market.marketId}
                  templateId={MarketLevelMarketTemplateId.ODDS_BOOST}
                  marketId={market.marketId}
                />
              );
            })}
            <EventStructuredData
              event={event}
              eventType={eventType}
              market={market}
              competition={competition}
            />
          </div>
        );
      })}
    </>
  );
};

SportsOddsBoostWidget.model = sportsOddsBoostWidgetModel;

export default withWidgetTestId(SportsOddsBoostWidget, 'sports-odds-boost', {
  className: styles.wrapper
});
